import type { ColumnDef } from '@tanstack/react-table';
import { Checkbox } from '@/components/ui/checkbox';
import type { TableRowData } from '../types';

/**
 * Stop row-level handlers (expansion / row click) from firing when the user
 * interacts with the selection checkbox — mirrors the Actions cell guard.
 */
function stopRowActivation(event: { stopPropagation: () => void }): void {
  event.stopPropagation();
}

/**
 * Synthetic leading checkbox column injected by `useDataTableInstance` when row
 * selection is enabled (KIR-3252). NOT a `ColumnConfig`: its node `header`/`cell`
 * render via `SingleRowHeader`'s fallback + `TableBody`'s `flexRender`.
 *
 * Two modes share the SAME `rowSelection` state with inverted meaning:
 * - keys mode (`selectAllAcrossPages=false`): `rowSelection` = the INCLUDED rows.
 *   Checkbox checked ⇔ row selected.
 * - select-all mode (`selectAllAcrossPages=true`): the user picked "all across
 *   pages", so `rowSelection` is repurposed as the EXCLUDED set. Checkbox
 *   display is inverted (a row reads checked unless it's been excluded), and
 *   toggling a row adds/removes it from the exclusion set.
 */
export function createSelectionColumn<TRow extends TableRowData>(
  selectAllAcrossPages = false,
  onSelectAllModeToggle?: () => void
): ColumnDef<TRow> {
  return {
    id: 'select',
    size: 40,
    enableSorting: false,
    header: ({ table }) => {
      // Select-all mode: the selection spans ALL pages, so the header is a
      // GLOBAL toggle, not a per-page one. Its checked/indeterminate state
      // reflects the whole cross-page exclusion set (`rowSelection` holds
      // exclusions) rather than just this page, and clicking it clears the
      // entire selection. Toggling only the current page here would leave the
      // other pages selected — the KIR-3252 "previous page persists" bug.
      if (selectAllAcrossPages) {
        const exclusionCount = Object.keys(
          table.getState().rowSelection
        ).length;
        return (
          <div
            className="flex items-center justify-center"
            onClick={stopRowActivation}
            onPointerDown={stopRowActivation}
          >
            <Checkbox
              aria-label="Select all rows on this page"
              // No exclusions → everything is selected (checked); any exclusion
              // → partial (indeterminate). Either way a click deselects all.
              checked={exclusionCount === 0}
              indeterminate={exclusionCount > 0}
              onChange={() => onSelectAllModeToggle?.()}
            />
          </div>
        );
      }

      const someOnPage = table.getIsSomePageRowsSelected();
      const allOnPage = table.getIsAllPageRowsSelected();
      const checked = allOnPage;
      const indeterminate = someOnPage && !allOnPage;
      return (
        <div
          className="flex items-center justify-center"
          onClick={stopRowActivation}
          onPointerDown={stopRowActivation}
        >
          <Checkbox
            aria-label="Select all rows on this page"
            checked={checked}
            indeterminate={indeterminate}
            onChange={() => table.toggleAllPageRowsSelected()}
          />
        </div>
      );
    },
    cell: ({ row }) => {
      const selected = row.getIsSelected();
      const checked = selectAllAcrossPages ? !selected : selected;
      return (
        <div
          className="flex items-center justify-center"
          onClick={stopRowActivation}
          onPointerDown={stopRowActivation}
        >
          <Checkbox
            // Row identity in the label so screen-reader users can tell which
            // alert a checkbox controls (WCAG 4.1.2 / 2.4.6) — `row.id` is the
            // domain key from `getRowId`.
            aria-label={`Select alert ${row.id}`}
            checked={checked}
            disabled={!selectAllAcrossPages && !row.getCanSelect()}
            // No-arg toggle — see header note: don't trust `e.target.checked`
            // (display is inverted in select-all mode).
            onChange={() => row.toggleSelected()}
          />
        </div>
      );
    },
  };
}
